import { useCallback, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInMinutes } from 'date-fns';
import { useNavigate, useParams } from 'react-router-dom';
import type { Socket } from 'socket.io-client';
import type { AuthUserResponse } from 'api/user/handlers/authUserHandler';
import type { LogoutUserResponse } from 'api/user/handlers/logoutUserHandler';
import type { AuthUserRequest } from 'common/user/AuthUserDocuments';
import type { LogoutUserRequest } from 'common/user/LogoutUserDocuments';
import type { UserClientDocument } from 'common/user/UserDocument';
import { useUserState } from 'client/state/UserState';
import Api from './Api';
import { SocketContext } from './Socket';

const loadSession = () =>
  new Promise<UserClientDocument>((resolve, reject) =>
    Api.post<AuthUserRequest, AuthUserResponse>('/user/auth', {})
      .then((response) => {
        const { status } = response;
        if (status == 'unauthorized') {
          // This will happen if trial account expires
          // TODO: is this the right stuff to do?
          window.localStorage.clear();
          if (window.location.pathname != '/login') window.location.href = '/login';
        }
        if (status !== 'ok') return reject(status);
        return resolve(response.user);
      })
      .catch((error) => {
        console.error(error);
        return reject('unexpected error');
      })
  );

export const useUser = () => {
  const navigate = useNavigate();
  const userState = useUserState();
  const queryClient = useQueryClient();

  const socket = useContext<Socket | null>(SocketContext);

  const updateSession = useCallback(async () => {
    queryClient.invalidateQueries(['user']);
    socket?.disconnect();
    socket?.connect();
  }, []);

  const logout = useCallback(
    async ({ skipRedirect = false }: { skipRedirect?: boolean } = {}) => {
      await Api.post<LogoutUserRequest, LogoutUserResponse>('/user/logout', {});

      userState.logout();
      window.localStorage.clear();

      if (socket) socket.disconnect();

      if (!skipRedirect) navigate('/login');
    },
    [socket]
  );

  const hasAccessRight = (accessRightID: number) => {
    if (!userState.user) return false;
    if (!userState.user.accessRightIDs) return false;
    return userState.user.accessRightIDs.indexOf(accessRightID) !== -1;
  };

  const anonymousTrial = userState.user?.anonymousTrial || null;
  const softLimitMinutesLeft = anonymousTrial ? Math.max(0, differenceInMinutes(new Date(anonymousTrial.timeSoftLimit), new Date())) : 0;
  const hardLimitMinutesLeft = anonymousTrial ? Math.max(0, differenceInMinutes(new Date(anonymousTrial.timeHardLimit), new Date())) : 0;

  // Log out from trial if we are past hard limit
  // TODO: Is react hook the right place for this.. works at least
  if (anonymousTrial && hardLimitMinutesLeft < 0) logout();

  return {
    user: userState.user,
    isLoggedIn: userState.loggedIn,
    isPendingUpdate: userState.isPendingUpdate,
    account: userState.account,
    login: userState.login,
    logout,
    hasAccessRight,
    updateSession,
    anonymousTrial: anonymousTrial ? { softLimitMinutesLeft, hardLimitMinutesLeft } : null,
  };
};

/** Get current selected account */
export const useAccount = () => {
  const { account: accountIdentifier } = useParams();
  const cachedAccount = useUserState((state) => state.account || null);
  const accounts = useUserState((state) => state.user?.accounts || null);
  const projects = useUserState((state) => state.user?.projects || null);

  const appendProjects = (account: UserClientDocument['accounts'][number]) => ({
    ...account,
    projects: (projects || []).filter((project) => project.accountID === account.id),
  });

  if (!accounts) return null;

  // Only a single account, always return that one
  if (accounts.length === 1) return appendProjects(accounts[0]);

  if (!(accountIdentifier || cachedAccount)) return null;

  const account = accounts.find((account) => account.identifier === (accountIdentifier || cachedAccount));
  if (!account) return null;

  return appendProjects(account);
};

export const UserSessionProvider = () => {
  useQuery({
    queryKey: ['user'],
    queryFn: () => loadSession(),
    staleTime: 60 * 1000, // Stale after 1 minute
    refetchInterval: 30 * 1000, // Refetch every 30 secs

    retryDelay: 2000,

    // Try to auth even if the user is offline
    networkMode: 'always',

    retry: (failureCount, error) => {
      // We have gotten a response from API that states our session to be invalid, logout immediately
      if (error === 'login required') return false;

      // If the error if due to something else, keep retrying without showing login screen
      return true;
    },

    // TODO: Error handling could be more graceful here...
    onError: () => useUserState.getState().logout(),
    onSuccess: (user) => useUserState.getState().login(user),
  });

  return null;
};
